import { api } from "../api/client.ts";

interface QBField {
  id: number;
  label: string;
  type: string;
  appearsByDefault: boolean;
  properties: Record<string, any>;
}

// Status badges – color mapping
const STATUS_COLORS: Record<string, string> = {
  "Planned": "#f0ad4e",
  "In Progress": "#5bc0de",
  "Inspection Done": "#5cb85c",
  "No Data": "#999",
  "Uploaded": "#5bc0de",
  "Processed": "#5cb85c",
};

// Field types that render as multiline blocks
const MULTILINE_TYPES = new Set(["text-multi-line"]);

// Field types rendered as status badges
const BADGE_TYPES = new Set(["text-multiple-choice"]);

// System / internal fields to skip in the UI
const SKIP_FIELD_IDS = new Set([1, 2, 3, 4, 5]);

// Address sub-field IDs are rendered as part of their parent
const _isSubField = (f: QBField) => !!f.properties.parentFieldId;

export class QuickBasePanel {
  private el: HTMLElement;
  private bodyEl: HTMLElement;
  private emptyEl: HTMLElement;
  private loadingEl: HTMLElement;
  private folderId = "";

  constructor(pane: HTMLElement) {
    this.el = pane;
    this.el.innerHTML = `
      <div class="qb-header">
        <span class="qb-title">QuickBase</span>
        <button class="qb-refresh" title="Refresh">&#x21bb;</button>
      </div>
      <div class="qb-loading">Loading...</div>
      <div class="qb-body"></div>
      <div class="qb-empty">No QuickBase record found.</div>
    `;

    this.bodyEl = this.el.querySelector(".qb-body")!;
    this.emptyEl = this.el.querySelector(".qb-empty")!;
    this.loadingEl = this.el.querySelector(".qb-loading")!;

    this.el.querySelector(".qb-refresh")!.addEventListener("click", () => this.refresh());

    this._showState("empty");
  }

  /** Called when a project is opened – stores the folder ID and fetches data. */
  async load(folderId: string): Promise<boolean> {
    this.folderId = folderId;
    return this._fetch();
  }

  /** Re-fetch data for the current folder. */
  async refresh(): Promise<void> {
    if (this.folderId) await this._fetch();
  }

  private async _fetch(): Promise<boolean> {
    this._showState("loading");
    try {
      const data = await api.quickbase.lookup(this.folderId);
      this._render(data.fields, data.record);
      this._showState("body");
      return true;
    } catch {
      this._showState("empty");
      return false;
    }
  }

  private _showState(state: "loading" | "body" | "empty"): void {
    this.loadingEl.style.display = state === "loading" ? "" : "none";
    this.bodyEl.style.display = state === "body" ? "" : "none";
    this.emptyEl.style.display = state === "empty" ? "" : "none";
  }

  private _render(fields: QBField[], record: Record<string, any>): void {
    this.bodyEl.innerHTML = "";

    // Separate badges (status fields) from the rest
    const badges: QBField[] = [];
    const regular: QBField[] = [];
    const multiline: QBField[] = [];

    // Collect address sub-field IDs to group under parent
    const addressSubIds = new Set<number>();
    for (const f of fields) {
      if (f.type === "address") {
        for (const cf of f.properties.compositeFields ?? []) {
          addressSubIds.add(cf.id);
        }
      }
    }

    for (const f of fields) {
      if (SKIP_FIELD_IDS.has(f.id)) continue;
      if (_isSubField(f)) continue; // rendered under parent address
      if (addressSubIds.has(f.id)) continue;

      const val = record[String(f.id)];
      if (val === undefined || val === null || val === "") continue;

      if (BADGE_TYPES.has(f.type)) {
        badges.push(f);
      } else if (MULTILINE_TYPES.has(f.type)) {
        multiline.push(f);
      } else {
        regular.push(f);
      }
    }

    // 1. Status badges row
    if (badges.length > 0) {
      const row = document.createElement("div");
      row.className = "qb-badges";
      for (const f of badges) {
        const val = String(record[String(f.id)] ?? "");
        const color = STATUS_COLORS[val] ?? "#888";
        const badge = document.createElement("span");
        badge.className = "qb-badge";
        badge.style.background = color;
        badge.textContent = val;
        badge.title = f.label;
        row.appendChild(badge);
      }
      this.bodyEl.appendChild(row);
    }

    // 2. Regular key-value fields
    for (const f of regular) {
      const val = record[String(f.id)];
      this.bodyEl.appendChild(this._renderField(f, val, record));
    }

    // 3. Multiline text blocks
    for (const f of multiline) {
      const val = record[String(f.id)];
      this.bodyEl.appendChild(this._renderMultiline(f, String(val)));
    }
  }

  private _renderField(
    f: QBField,
    value: any,
    record: Record<string, any>,
  ): HTMLElement {
    const row = document.createElement("div");
    row.className = "qb-field";

    const label = document.createElement("span");
    label.className = "qb-field-label";
    label.textContent = f.label;

    const val = document.createElement("span");
    val.className = "qb-field-value";

    if (f.type === "address") {
      val.innerHTML = this._formatAddress(f, record);
    } else if (f.type === "url") {
      const a = document.createElement("a");
      a.href = String(value);
      a.target = "_blank";
      a.rel = "noopener";
      a.textContent = "Open link";
      a.className = "qb-link";
      val.appendChild(a);
    } else if (f.type === "currency") {
      const sym = f.properties.currencySymbol ?? "$";
      const dec = f.properties.decimalPlaces ?? 2;
      val.textContent = `${sym}${Number(value).toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
    } else if (f.type === "date" || f.type === "timestamp") {
      val.textContent = value ? new Date(value).toLocaleDateString("en-US") : "";
    } else {
      val.textContent = String(value);
    }

    row.appendChild(label);
    row.appendChild(val);
    return row;
  }

  private _formatAddress(
    parent: QBField,
    record: Record<string, any>,
  ): string {
    const composites: { key: string; id: number }[] =
      parent.properties.compositeFields ?? [];
    const parts: string[] = [];
    for (const cf of composites) {
      const v = record[String(cf.id)];
      if (v) parts.push(_escHtml(String(v)));
    }
    return parts.join(", ");
  }

  private _renderMultiline(f: QBField, value: string): HTMLElement {
    const block = document.createElement("div");
    block.className = "qb-multiline";

    const label = document.createElement("div");
    label.className = "qb-multiline-label";
    label.textContent = f.label;

    const text = document.createElement("div");
    text.className = "qb-multiline-text";
    text.textContent = value;

    block.appendChild(label);
    block.appendChild(text);
    return block;
  }

  dispose(): void {
    this.el.innerHTML = "";
  }
}

function _escHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
